import { getObjectSchema, isHaspProperty, toWireKey } from './wireFormat.js';
import { useUndoStore } from '../../../history/undoStore.js';
import ObjectConversionCommand from '../../../history/commands/ObjectConversionCommand.js';

const IDENTITY_KEYS = new Set(['haspid', 'parentid']);
const GEOMETRY_KEYS = new Set(['x', 'y', 'w', 'h', 'width', 'height', 'size']);

// These are editor/runtime constraints, not portable object state. They are
// deliberately resolved from the target schema when the replacement is
// imported; copying them would make a converted object retain the source
// widget's resize limits.
const TARGET_SCHEMA_METADATA_KEYS = new Set(['min_width', 'min_height']);

// Button `val` is an ON/OFF value only when the existing button is configured
// as a toggle. A normal push button must not become ON merely because it has a
// transient/default value. Switches are inherently boolean, so converting one
// to a button enables the button's existing V2 toggle/checkable behaviour.
const pairSpecificRules = {
    'btn->switch': {
        shouldCopy(source, property) {
            return property !== 'val' || source.properties?.toggle === true;
        },
    },
    'switch->btn': {
        apply(config, source) {
            config.toggle = true;
            if (source.properties?.val !== undefined) config.val = clone(source.properties.val);
        },
    },
};

function clone(value) {
    return value && typeof value === 'object'
        ? JSON.parse(JSON.stringify(value))
        : value;
}

function conversionDefinition(sourceType, targetType) {
    const raw = getObjectSchema(sourceType)?.convertTo;
    if (Array.isArray(raw)) return raw.includes(targetType) ? {} : null;
    if (!raw || typeof raw !== 'object' || !Object.hasOwn(raw, targetType)) return null;
    return raw[targetType] ?? {};
}

/** Return the schema-authorized conversion targets for one object type. */
export function getObjectConversionTargets(sourceType) {
    const raw = getObjectSchema(sourceType)?.convertTo;
    const candidates = Array.isArray(raw) ? raw : Object.keys(raw ?? {});
    return candidates.filter(target => target !== sourceType && !!getObjectSchema(target));
}

export function getObjectTypeDisplayName(objType) {
    const schemaLabel = getObjectSchema(objType)?.displayName;
    if (schemaLabel) return schemaLabel;
    return String(objType ?? '').replace(/(^|[_-])\w/g, match => match.toUpperCase()).replace(/_/g, ' ');
}

/** Re-check the schema authorization immediately before applying a conversion. */
export function isObjectConversionAllowed(sourceType, targetType) {
    return !!conversionDefinition(sourceType, targetType);
}

/**
 * Capture the explicit, semantic state needed by the conversion mapper.
 * Defaults and theme values are deliberately not copied into styles: the target
 * widget must resolve those through its normal ConfigManager/StyleResolver path.
 */
export function captureConversionSource(node) {
    const parent = node?.getParent?.();
    const page = findPage(node);
    const styles = {};

    for (const partName of node?.getPartNames?.() ?? []) {
        const states = {};
        for (const stateName of ['default', ...(node.getSupportedStates?.() ?? [])]) {
            const properties = {};
            for (const propName of Object.keys(node.getStyleDefinition?.(partName)?.properties ?? {})) {
                if (!node.hasExplicitStyle?.(partName, propName, stateName)) continue;
                properties[propName] = stateName === 'default'
                    ? node.styleProp(partName, propName, 'default')
                    : node.stateStyleProp(partName, stateName, propName);
            }
            if (stateName === 'default' || Object.keys(properties).length > 0) {
                states[stateName] = { properties };
            }
        }
        styles[partName] = { states };
    }

    const properties = {};
    for (const propName of node?.properties ?? []) {
        if (IDENTITY_KEYS.has(propName)
            || GEOMETRY_KEYS.has(propName)
            || TARGET_SCHEMA_METADATA_KEYS.has(propName)) continue;

        const value = topLevelPropertyValue(node, propName);
        if (value === undefined || !isConversionInstanceValue(node, propName, value)) continue;
        properties[propName] = clone(value);
    }

    return {
        obj: node.obj,
        uuid: node.id?.(),
        haspid: node.haspid,
        x: node.x?.(),
        y: node.y?.(),
        w: node.width?.(),
        h: node.height?.(),
        page: page?.haspid,
        parentid: parent && parent.obj !== 'page' && parent.obj !== 'template_page'
            ? parent.haspid
            : undefined,
        comment: node.comment,
        custom_component_id: node.custom_component_id,
        custom_component_name: node.custom_component_name,
        properties,
        styles,
        actions: clone(node.actions ?? []),
        gpio_bindings: clone(node.gpio_bindings),
        hass_config: clone(node.hass_config),
        childCount: node.getChildren?.().filter(child => child.obj && child.obj !== 'text').length ?? 0,
    };
}

function topLevelPropertyValue(node, property) {
    const field = isHaspProperty(node?.schemaV2, property) ? `hasp_${property}` : property;
    const value = node?.[field];
    return typeof value === 'function' ? undefined : value;
}

/**
 * Plain top-level fields are prefilled with defaults by ConfigManager. Only
 * preserve values that were explicit in the input or that the user changed
 * away from the current source default. This keeps source defaults/metadata
 * out of the target config while retaining real instance values.
 */
function isConversionInstanceValue(node, property, value) {
    if (node?._explicitTopLevelProperties?.has(property)) return true;

    const defaults = node?._topLevelDefaultValues;
    if (defaults && Object.hasOwn(defaults, property)) return value !== defaults[property];
    return true;
}

/**
 * Build the flat import representation used by HaspObjectLayer.importObject.
 * The same result is used by the preview and by the confirmed conversion, so
 * preview and commit cannot drift apart.
 */
export function buildObjectConversionPreview(source, targetType) {
    return buildObjectConfig(source, targetType, true);
}

function buildObjectConfig(source, targetType, enforceAuthorization) {
    const sourceType = source?.obj;
    const sourceSchema = getObjectSchema(sourceType);
    const targetSchema = getObjectSchema(targetType);
    if (!sourceSchema || !targetSchema || (enforceAuthorization && !isObjectConversionAllowed(sourceType, targetType))) {
        throw new Error(`Object conversion ${sourceType} → ${targetType} is not allowed by Schema V2.`);
    }

    const definition = conversionDefinition(sourceType, targetType) ?? {};
    const pairRule = pairSpecificRules[`${sourceType}->${targetType}`];
    const targetProperties = targetSchema.properties ?? {};
    const config = {
        obj: targetType,
        id: source.haspid,
        x: source.x,
        y: source.y,
        w: source.w,
        h: source.h,
        page: source.page,
        parentid: source.parentid,
        comment: source.comment,
        custom_component_id: source.custom_component_id,
        custom_component_name: source.custom_component_name,
    };

    for (const [property, value] of Object.entries(source.properties ?? {})) {
        if (property === 'obj'
            || TARGET_SCHEMA_METADATA_KEYS.has(property)
            || !Object.hasOwn(targetProperties, property)) continue;
        if (pairRule?.shouldCopy && !pairRule.shouldCopy(source, property)) continue;
        if (!compatibleProperty(sourceSchema.properties?.[property], targetProperties[property])) continue;
        config[property] = property === 'swipe' && typeof value === 'boolean'
            ? (value ? 1 : 0)
            : clone(value);
    }

    const propertyMap = definition.propertyMap ?? definition.properties ?? {};
    for (const [sourceProperty, targetProperty] of Object.entries(propertyMap)) {
        if (source.properties?.[sourceProperty] !== undefined && Object.hasOwn(targetProperties, targetProperty)) {
            config[targetProperty] = clone(source.properties[sourceProperty]);
        }
    }

    pairRule?.apply?.(config, source);

    const targetStates = new Set(['default', ...(targetSchema.states ?? [])]);
    for (const [partName, targetPart] of Object.entries(targetSchema.parts ?? {})) {
        const sourcePart = source.styles?.[partName];
        if (!sourcePart) continue;
        const sourcePartProperties = sourceSchema.parts?.[partName]?.properties ?? {};
        for (const [stateName, stateData] of Object.entries(sourcePart.states ?? {})) {
            if (!targetStates.has(stateName)) continue;
            for (const [property, value] of Object.entries(stateData.properties ?? {})) {
                if (!Object.hasOwn(targetPart.properties ?? {}, property)) continue;
                if (!Object.hasOwn(sourcePartProperties, property)) continue;
                if (!compatibleProperty(sourcePartProperties[property], targetPart.properties[property])) continue;
                config[toWireKey(partName, property, targetPart, stateName)] = clone(value);
            }
        }
    }

    // These are Designer data rather than widget properties. Keep them even if
    // the target exposes a different event editor; unsupported event data is
    // retained for validation/export instead of being silently discarded.
    if (Array.isArray(source.actions) && source.actions.length > 0) config.actions = clone(source.actions);
    if (source.gpio_bindings !== undefined) config.gpio_bindings = clone(source.gpio_bindings);
    if (source.hass_config !== undefined) config.hass_config = clone(source.hass_config);
    return config;
}

function compatibleProperty(sourceDefinition, targetDefinition) {
    if (!sourceDefinition || !targetDefinition) return false;
    if (sourceDefinition.type === targetDefinition.type) return true;
    return ['int', 'float'].includes(sourceDefinition.type)
        && ['int', 'float'].includes(targetDefinition.type);
}

/** Capture a complete, reloadable wire config for undo and redo. */
export function captureObjectConversionConfig(node) {
    const source = captureConversionSource(node);
    const config = buildObjectConfig(source, node.obj, false);
    config.width = config.w ?? node.width?.();
    config.height = config.h ?? node.height?.();
    config._uuid = node.id?.();
    return config;
}

/** Apply one confirmed conversion and record it as one complete undo entry. */
export function convertObjectType(node, targetType) {
    if (!node || !isObjectConversionAllowed(node.obj, targetType)) {
        throw new Error(`Object conversion ${node?.obj} → ${targetType} is not allowed by Schema V2.`);
    }
    const layer = node.getLayer?.();
    if (!layer) throw new Error('Cannot convert an object without a Designer layer.');

    const source = captureConversionSource(node);
    const beforeConfig = captureObjectConversionConfig(node);
    const afterConfig = buildObjectConversionPreview(source, targetType);
    const replacement = layer.replaceObjectWithConfig(node.id(), afterConfig);
    if (!replacement) throw new Error('The object conversion could not be applied.');

    useUndoStore().push(new ObjectConversionCommand({
        objectUuid: node.id(),
        beforeConfig,
        afterConfig,
        pageUuid: findPage(replacement)?.id?.(),
        sourceType: node.obj,
        targetType,
    }));
    return replacement;
}

function findPage(node) {
    let current = node;
    while (current && current.obj !== 'page' && current.obj !== 'template_page') {
        current = current.getParent?.();
    }
    return current;
}
